import type React from 'react';
import { cn } from '@/lib/utils';

export type DaySchedule = { open: string; close: string } | null;
export type WorkingHoursValue = Record<string, DaySchedule>;

interface WorkingHoursEditorProps {
  value: WorkingHoursValue;
  onChange: (value: WorkingHoursValue) => void;
  className?: string;
}

const DAYS = [
  { key: 'monday', label: 'Понедельник', short: 'Пн' },
  { key: 'tuesday', label: 'Вторник', short: 'Вт' },
  { key: 'wednesday', label: 'Среда', short: 'Ср' },
  { key: 'thursday', label: 'Четверг', short: 'Чт' },
  { key: 'friday', label: 'Пятница', short: 'Пт' },
  { key: 'saturday', label: 'Суббота', short: 'Сб' },
  { key: 'sunday', label: 'Воскресенье', short: 'Вс' },
] as const;

const WorkingHoursEditor: React.FC<WorkingHoursEditorProps> = ({
  value,
  onChange,
  className,
}) => {
  const toggleDay = (dayKey: string) => {
    const current = value[dayKey];
    onChange({
      ...value,
      [dayKey]: current ? null : { open: '09:00', close: '18:00' },
    });
  };

  const updateTime = (dayKey: string, field: 'open' | 'close', time: string) => {
    const current = value[dayKey];
    if (!current) return;
    onChange({
      ...value,
      [dayKey]: { ...current, [field]: time },
    });
  };

  return (
    <div className={cn('space-y-2', className)}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Режим работы
      </label>

      <div className="space-y-1.5">
        {DAYS.map(({ key, label, short }) => {
          const schedule = value[key];
          const isActive = schedule !== null && schedule !== undefined;

          return (
            <div
              key={key}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-200',
                isActive
                  ? 'border-primary-200 bg-primary-50/50'
                  : 'border-gray-100 bg-gray-50/50',
              )}
            >
              <button
                type="button"
                onClick={() => toggleDay(key)}
                className={cn(
                  'flex-shrink-0 w-10 h-6 rounded-full transition-all duration-200 relative',
                  isActive ? 'bg-primary-500' : 'bg-gray-300',
                )}
                aria-label={`Переключить ${label}`}
              >
                <div
                  className={cn(
                    'absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200',
                    isActive ? 'translate-x-[18px]' : 'translate-x-0.5',
                  )}
                />
              </button>

              <span className="hidden sm:inline text-sm font-medium text-gray-700 w-28 flex-shrink-0">
                {label}
              </span>
              <span className="sm:hidden text-sm font-medium text-gray-700 w-8 flex-shrink-0">
                {short}
              </span>

              {isActive ? (
                <div className="flex items-center gap-1.5 ml-auto">
                  <input
                    type="time"
                    value={schedule.open}
                    onChange={(e) => updateTime(key, 'open', e.target.value)}
                    className="px-2 py-1 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 w-[100px]"
                  />
                  <span className="text-gray-400 text-xs">—</span>
                  <input
                    type="time"
                    value={schedule.close}
                    onChange={(e) => updateTime(key, 'close', e.target.value)}
                    className="px-2 py-1 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 w-[100px]"
                  />
                </div>
              ) : (
                <span className="ml-auto text-sm text-gray-400 italic">Выходной</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkingHoursEditor;
